import { Home } from 'lucide-react'
import { lazy, PropsWithChildren, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'

import { AuthPage } from '@/app/auth/page'
import { ErrorPage } from '@/app/error/page'
import { NotFoundPage } from '@/app/not-found/page'
import { ManagementPage } from '@/app/panel/management/page'
import { ManagementPriceListPage } from '@/app/panel/management/price-list/page'
import { PanelPage } from '@/app/panel/page'
import { TerminalsPage } from '@/app/panel/terminals/page'
import { Spinner } from '@/components/ui/spinner'
import { BoundaryRoute } from '@/routes/boundary-route'
import { FeaturedRoute } from '@/routes/featured-route'
import { PrivateRoute } from '@/routes/private-route'
import { PublicRoute } from '@/routes/public-route'
import { Feature } from '@/schemas/feature'

const LoginPage = lazy(() =>
  import('@/app/auth/login/page').then((module) => ({
    default: module.LoginPage,
  })),
)
const LoginTokenPage = lazy(() =>
  import('@/app/auth/login-token/page').then((module) => ({
    default: module.LoginTokenPage,
  })),
)
const CreateAccountPage = lazy(() =>
  import('@/app/auth/create-account/page').then((module) => ({
    default: module.CreateAccountPage,
  })),
)
const ForgotPasswordPage = lazy(() =>
  import('@/app/auth/forgot-password/page').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
)
const ResetPasswordPage = lazy(() =>
  import('@/app/auth/reset-password/page').then((module) => ({
    default: module.ResetPasswordPage,
  })),
)
const DashboardPage = lazy(() =>
  import('@/app/panel/dashboard/page').then((module) => ({
    default: module.DashboardPage,
  })),
)
const EventsPage = lazy(() =>
  import('@/app/panel/events/page').then((module) => ({
    default: module.EventsPage,
  })),
)
const EventDetailsPage = lazy(() =>
  import('@/app/panel/events/[id]/page').then((module) => ({
    default: module.EventDetailsPage,
  })),
)
const EventDetailRefundsPage = lazy(() =>
  import('@/app/panel/events/[id]/refunds/page').then((module) => ({
    default: module.EventDetailRefundsPage,
  })),
)
const EventDetailRechargesPage = lazy(() =>
  import('@/app/panel/events/[id]/recharges/page').then((module) => ({
    default: module.EventDetailRechargesPage,
  })),
)
const SettingsPage = lazy(() =>
  import('@/app/panel/settings/page').then((module) => ({
    default: module.SettingsPage,
  })),
)
const SettingsTokenPage = lazy(() =>
  import('@/app/panel/settings/token/page').then((module) => ({
    default: module.SettingsTokenPage,
  })),
)
const SettingsCompanyPage = lazy(() =>
  import('@/app/panel/settings/company/page').then((module) => ({
    default: module.SettingsCompanyPage,
  })),
)

const WrapperWithLoading: React.FC<PropsWithChildren> = ({ children }) => (
  <Suspense fallback={<Spinner />}>{children}</Suspense>
)

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <PrivateRoute>
        <PanelPage />
      </PrivateRoute>
    ),
    errorElement: <BoundaryRoute />,
    handle: {
      breadcrumb: {
        icon: <Home className="text-sky-500" size={16} />,
        title: 'Dashboard',
      },
    },
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" />,
      },
      {
        path: 'dashboard',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <WrapperWithLoading>
                <DashboardPage />
              </WrapperWithLoading>
            ),
          },
        ],
      },
      {
        path: 'eventos',
        element: <Outlet />,
        handle: {
          breadcrumb: {
            title: 'Eventos',
          },
        },
        children: [
          {
            index: true,
            element: (
              <WrapperWithLoading>
                <EventsPage />
              </WrapperWithLoading>
            ),
          },
          {
            path: ':id',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <WrapperWithLoading>
                    <EventDetailsPage />
                  </WrapperWithLoading>
                ),
              },
              {
                path: 'reembolsos',
                element: (
                  <WrapperWithLoading>
                    <EventDetailRefundsPage />
                  </WrapperWithLoading>
                ),
              },
              {
                path: 'recargas',
                element: (
                  <WrapperWithLoading>
                    <EventDetailRechargesPage />
                  </WrapperWithLoading>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'cadastros',
        element: (
          <WrapperWithLoading>
            <FeaturedRoute feature={Feature['Route.Management']}>
              <ManagementPage />
            </FeaturedRoute>
          </WrapperWithLoading>
        ),
        handle: {
          breadcrumb: {
            title: 'Cadastros',
          },
        },
        children: [
          {
            index: true,
            element: <Navigate to="/cadastros/lista-de-precos" />,
          },
          {
            path: 'lista-de-precos',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <WrapperWithLoading>
                    <FeaturedRoute
                      feature={Feature['Route.Management.PriceList']}
                    >
                      <ManagementPriceListPage />
                    </FeaturedRoute>
                  </WrapperWithLoading>
                ),
              },
            ],
          },
          {
            path: 'layout',
            element: (
              <WrapperWithLoading>
                <FeaturedRoute feature={Feature['Route.Management.Layout']}>
                  <h1>Em breve...</h1>
                </FeaturedRoute>
              </WrapperWithLoading>
            ),
          },
          {
            path: 'eventos',
            element: (
              <WrapperWithLoading>
                <FeaturedRoute feature={Feature['Route.Management.Events']}>
                  <h1>Em breve...</h1>
                </FeaturedRoute>
              </WrapperWithLoading>
            ),
          },
        ],
      },
      {
        path: 'terminais',
        element: (
          <WrapperWithLoading>
            <FeaturedRoute feature={Feature['Route.Terminals']}>
              <TerminalsPage />
            </FeaturedRoute>
          </WrapperWithLoading>
        ),
        handle: {
          breadcrumb: {
            title: 'Terminais',
          },
        },
        children: [
          {
            index: true,
            element: <Navigate to="/terminais/opcoes" />,
          },
          {
            path: 'opcoes',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <WrapperWithLoading>
                    <FeaturedRoute
                      feature={Feature['Route.Terminals.Settings']}
                    >
                      <h1>Em breve...</h1>
                    </FeaturedRoute>
                  </WrapperWithLoading>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'configuracoes',
        element: (
          <WrapperWithLoading>
            <SettingsPage />
          </WrapperWithLoading>
        ),
        handle: {
          breadcrumb: {
            title: 'Configurações',
          },
        },
        children: [
          {
            index: true,
            element: <Navigate to="/configuracoes/token" />,
          },
          {
            path: 'token',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <WrapperWithLoading>
                    <SettingsTokenPage />
                  </WrapperWithLoading>
                ),
              },
            ],
          },
          {
            path: 'empresa',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <WrapperWithLoading>
                    <SettingsCompanyPage />
                  </WrapperWithLoading>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'auth',
    element: (
      <PublicRoute>
        <AuthPage />
      </PublicRoute>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperWithLoading>
            <LoginPage />
          </WrapperWithLoading>
        ),
      },
      {
        path: 'login-token',
        element: (
          <WrapperWithLoading>
            <LoginTokenPage />
          </WrapperWithLoading>
        ),
      },
      {
        path: 'registrar',
        element: (
          <WrapperWithLoading>
            <CreateAccountPage />
          </WrapperWithLoading>
        ),
      },
      {
        path: 'esqueceu-senha',
        element: (
          <WrapperWithLoading>
            <ForgotPasswordPage />
          </WrapperWithLoading>
        ),
      },
      {
        path: 'resetar-senha/:token',
        element: (
          <WrapperWithLoading>
            <ResetPasswordPage />
          </WrapperWithLoading>
        ),
      },
    ],
  },
  {
    path: '/erro',
    element: <ErrorPage />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
])

export { router }
